import { Edit } from "@mui/icons-material";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { CandidateStatusColor } from "../../Enums/CandidateStatusColor";
import {
  useCandidateStatusDefinitionsQuery,
  useSetCandidateStatusMutation,
  useUpdateStatusDefinitionMutation,
} from "../../Queries/CandidateStatusQueries";
import { useNotification } from "../../State/Hooks/notificationHooks";
import { ICandidateStatusDefinition } from "../../Typings/CandidateStatus";
import { CandidateStatusIcon } from "./CandidateStatusIcon";
import { CANDIDATE_STATUS_DEFAULTS } from "./statusDefaults";

interface CandidateStatusPickerProps {
  open: boolean;
  onClose: () => void;
  candidateId: string;
  currentStatusColor: string;
}

const normalizeColor = (value: string): CandidateStatusColor =>
  (Object.values(CandidateStatusColor) as string[]).includes(value)
    ? (value as CandidateStatusColor)
    : CandidateStatusColor.White;

export const CandidateStatusPicker: React.FC<CandidateStatusPickerProps> = ({
  open,
  onClose,
  candidateId,
  currentStatusColor,
}) => {
  const { data: definitions } = useCandidateStatusDefinitionsQuery();
  const setCandidateStatus = useSetCandidateStatusMutation();
  const updateDefinition = useUpdateStatusDefinitionMutation();
  const { displayError } = useNotification();

  const rows = useMemo<ICandidateStatusDefinition[]>(
    () => (definitions && definitions.length ? [...definitions] : [...CANDIDATE_STATUS_DEFAULTS]),
    [definitions],
  );

  const [selectedColor, setSelectedColor] = useState<CandidateStatusColor>(
    normalizeColor(currentStatusColor),
  );
  const [editingColor, setEditingColor] = useState<CandidateStatusColor | null>(null);
  const [editLabel, setEditLabel] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [pendingEdit, setPendingEdit] = useState<{
    color: CandidateStatusColor;
    label: string;
    description: string;
  } | null>(null);

  useEffect(() => {
    if (open) {
      setSelectedColor(normalizeColor(currentStatusColor));
      setEditingColor(null);
      setEditLabel("");
      setEditDescription("");
      setPendingEdit(null);
    }
  }, [open, currentStatusColor]);

  const handleApply = () => {
    setCandidateStatus.mutate(
      { candidateId, statusColor: selectedColor },
      {
        onSuccess: () => onClose(),
        onError: () => displayError("Failed to update candidate status. Please try again."),
      },
    );
  };

  const startEdit = (row: ICandidateStatusDefinition) => {
    setEditingColor(row.color);
    setEditLabel(row.label);
    setEditDescription(row.description ?? "");
  };

  const cancelEdit = () => {
    setEditingColor(null);
    setEditLabel("");
    setEditDescription("");
  };

  const requestEditConfirmation = (color: CandidateStatusColor) => {
    setPendingEdit({ color, label: editLabel.trim(), description: editDescription.trim() });
  };

  const confirmEdit = () => {
    if (!pendingEdit) return;
    updateDefinition.mutate(pendingEdit, {
      onSuccess: () => {
        setPendingEdit(null);
        cancelEdit();
      },
      onError: () => displayError("Failed to update status label. Please try again."),
    });
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" data-test="candidate-status-picker-modal">
        <DialogTitle>Set Candidate Status</DialogTitle>
        <DialogContent dividers>
          <Stack gap={1}>
            {rows.map((row) => {
              const isSelected = row.color === selectedColor;
              const isEditing = editingColor === row.color;
              return (
                <Box
                  key={row.color}
                  data-test={`status-option-${row.color}`}
                  onClick={isEditing ? undefined : () => setSelectedColor(row.color)}
                  sx={(theme) => ({
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 1.5,
                    padding: 1,
                    borderRadius: 1,
                    border: `1px solid ${isSelected ? theme.palette.primary.main : theme.palette.divider}`,
                    backgroundColor: isSelected ? theme.palette.action.selected : "transparent",
                    cursor: isEditing ? "default" : "pointer",
                    "&:hover": {
                      backgroundColor: isEditing ? "transparent" : theme.palette.action.hover,
                    },
                  })}
                >
                  <Box sx={{ pt: 0.5 }}>
                    <CandidateStatusIcon statusColor={row.color} size="medium" />
                  </Box>
                  {isEditing ? (
                    <Stack gap={1} sx={{ flex: 1 }}>
                      <TextField
                        size="small"
                        label="Label"
                        value={editLabel}
                        onChange={(e) => setEditLabel(e.target.value)}
                        inputProps={{ "data-test": `status-edit-label-${row.color}`, maxLength: 50 }}
                      />
                      <TextField
                        size="small"
                        label="Description"
                        value={editDescription}
                        onChange={(e) => setEditDescription(e.target.value)}
                        inputProps={{ "data-test": `status-edit-description-${row.color}`, maxLength: 200 }}
                        multiline
                        minRows={2}
                      />
                      <Stack direction="row" gap={1} justifyContent="flex-end">
                        <Button size="small" onClick={cancelEdit} disabled={updateDefinition.isLoading}>
                          Cancel
                        </Button>
                        <Button
                          size="small"
                          variant="contained"
                          onClick={() => requestEditConfirmation(row.color)}
                          disabled={!editLabel.trim() || updateDefinition.isLoading}
                          data-test={`status-edit-save-${row.color}`}
                        >
                          Save
                        </Button>
                      </Stack>
                    </Stack>
                  ) : (
                    <Stack sx={{ flex: 1 }}>
                      <Typography variant="subtitle2">{row.label}</Typography>
                      {row.description && (
                        <Typography variant="body2" color="text.secondary">
                          {row.description}
                        </Typography>
                      )}
                    </Stack>
                  )}
                  {!row.isSystemManaged && !isEditing && (
                    <IconButton
                      size="small"
                      aria-label={`Edit ${row.label}`}
                      data-test={`status-edit-${row.color}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        startEdit(row);
                      }}
                    >
                      <Edit fontSize="small" />
                    </IconButton>
                  )}
                </Box>
              );
            })}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={setCandidateStatus.isLoading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleApply}
            disabled={setCandidateStatus.isLoading || editingColor !== null}
            data-test="status-apply-btn"
          >
            Apply
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={pendingEdit !== null}
        onClose={() => setPendingEdit(null)}
        data-test="candidate-status-edit-confirm"
      >
        <DialogTitle>Update status label?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will update the status label and description for all candidates in your organization.
            Continue?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingEdit(null)} disabled={updateDefinition.isLoading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={confirmEdit}
            disabled={updateDefinition.isLoading}
            data-test="status-edit-confirm-btn"
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
